"use client";

import { useLocale } from "next-intl";
import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DemoContentCollection, DemoContentItem, DemoContentSet } from "@/lib/demo-content-types";
import { getLocaleConfig } from "@/i18n/locales";
import { convertLanguageCodeForHtml } from "@/utils/language-conversion";
import { getLanguages } from "@/app/[locale]/actions/languages";

let cachedDemoContent: DemoContentCollection | null = null;

// Cache the randomly selected item per locale so all components show the same item
const cachedItemByLocale: Record<string, DemoContentItem> = {};

// Cache the randomly selected solution index for each problem (keyed by item + problem index)
const cachedSolutionIndex: Record<string, number> = {};

function getLanguageNameFromId(
  languageId: string | null | undefined,
  fallback: string,
  languageNameById?: Map<string, string>
): string {
  if (!languageId) {
    return fallback;
  }

  const localizedName = languageNameById?.get(languageId);
  if (localizedName) {
    return localizedName;
  }

  const localeId = convertLanguageCodeForHtml(languageId);
  const config = getLocaleConfig(localeId);
  if (config?.name) {
    return config.name;
  }

  if (typeof Intl !== "undefined" && typeof Intl.DisplayNames !== "undefined") {
    const displayNames = new Intl.DisplayNames(["en"], { type: "language" });
    const displayName = displayNames.of(localeId);
    if (displayName) {
      return displayName;
    }
  }

  return languageId;
}

/**
 * Get a stable random solution index for a specific problem.
 * Caches the result so subsequent renders return the same index.
 */
function getRandomSolutionIndex(item: DemoContentItem, problemIndex: number, solutionCount: number): number {
  const cacheKey = `${item.localeId}:${item.outputLanguageId}:${problemIndex}`;

  if (cacheKey in cachedSolutionIndex) {
    return cachedSolutionIndex[cacheKey];
  }

  const randomIndex = Math.floor(Math.random() * solutionCount);
  cachedSolutionIndex[cacheKey] = randomIndex;
  return randomIndex;
}

/**
 * Select a random item from items matching the given localeId.
 * Caches the result so all components on the page show the same item.
 */
function getRandomItemForLocale(set: DemoContentSet, localeId: string): DemoContentItem | null {
  if (cachedItemByLocale[localeId]) {
    return cachedItemByLocale[localeId];
  }

  if (!set?.items?.length) return null;

  const matchingItems = set.items.filter(item => item.localeId === localeId);

  if (matchingItems.length > 0) {
    const randomIndex = Math.floor(Math.random() * matchingItems.length);
    cachedItemByLocale[localeId] = matchingItems[randomIndex];
    return cachedItemByLocale[localeId];
  }

  // Fallback: no items match this locale, return first item
  return set.items[0];
}


export function useDemoContent() {
  const locale = useLocale();
  const [demoContent, setDemoContent] = useState<DemoContentCollection | null>(cachedDemoContent);
  const [isLoading, setIsLoading] = useState(!cachedDemoContent);

  const { data: languages = [] } = useQuery({
    queryKey: ["languages", locale],
    queryFn: () => getLanguages(locale),
    staleTime: Infinity,
  });

  const languageNameById = useMemo(
    () => new Map(languages.map((language) => [language.id, language.name])),
    [languages]
  );

  useEffect(() => {
    if (cachedDemoContent) {
      setDemoContent(cachedDemoContent);
      setIsLoading(false);
      return;
    }

    fetch("/data/demo-content.json")
      .then((res) => {
        if (!res.ok) {
          console.warn(`Demo content fetch failed: HTTP ${res.status}`);
          throw new Error(`HTTP ${res.status}`);
        }
        const contentType = res.headers.get("content-type");
        if (!contentType?.includes("application/json")) {
          console.warn(`Demo content fetch failed: Content-Type is ${contentType}`);
          throw new Error("Response is not JSON");
        }
        return res.json();
      })
      .then((data: DemoContentCollection) => {
        cachedDemoContent = data;
        setDemoContent(data);
        setIsLoading(false);
      })
      .catch(() => {
        // Silently fail - component will use fallback defaults
        setIsLoading(false);
      });
  }, []);

  // Select the appropriate demo set and item based on locale
  const { selectedSet, selectedItem } = useMemo((): { selectedSet: DemoContentSet | null; selectedItem: DemoContentItem | null } => {
    if (!demoContent?.demoSets) return { selectedSet: null, selectedItem: null };

    // Chinese users get the 'zh' set, everyone else gets the 'en' set
    const setId = locale === "zh" ? "zh" : "en";
    const set = demoContent.demoSets[setId];
    if (!set?.items?.length) return { selectedSet: null, selectedItem: null };

    // Get a random item for this locale (cached so all components show the same)
    const item = getRandomItemForLocale(set, locale);

    return {
      selectedSet: set,
      selectedItem: item,
    };
  }, [demoContent, locale]);

  // Get the source text from the selected set
  const sourceText = selectedSet?.sourceText ?? null;
  const sourceLanguageId = selectedSet?.sourceLanguageId ?? null;
  const targetLanguageId = selectedItem?.outputLanguageId ?? null;

  const sourceLanguageName = useMemo(() => {
    return getLanguageNameFromId(selectedSet?.sourceLanguageId, "English", languageNameById);
  }, [selectedSet?.sourceLanguageId, languageNameById]);

  // Get target language name from outputLanguageId
  const targetLanguageName = useMemo(() => {
    return getLanguageNameFromId(selectedItem?.outputLanguageId, "German", languageNameById);
  }, [selectedItem?.outputLanguageId, languageNameById]);

  return {
    isLoading,
    sourceText,
    sourceLanguageId,
    sourceLanguageName,
    selectedItem,
    targetLanguageId,
    targetLanguageName,
    locale,
  };
}

/**
 * Maps Starky Sense problems to the contextItems format used by StarkySenseDialogMock.
 * Uses cached random indices so the same solution is shown across re-renders.
 */
export function mapProblemsToContextItems(
  item: DemoContentItem | null
): Array<{
  label: string;
  explanation: string;
  selectedOption: string;
}> {
  if (!item?.starkySenseOutput?.problems?.length) {
    return [];
  }

  return item.starkySenseOutput.problems.map((problem, problemIndex) => {
    const solutionIndex = getRandomSolutionIndex(item, problemIndex, problem.solutions.length);
    const selectedSolution = problem.solutions[solutionIndex];

    return {
      label: `@${problem.subject}`,
      explanation: problem.description,
      selectedOption: selectedSolution?.description ?? "",
    };
  });
}
